// Platform → Services (R12, screen 45): everything a running install needs
// that does not have to live in .env — Stripe keys, the mail server, upload
// storage, the public URLs every emailed link is composed from, the captcha,
// custom domains and the AI form builder.
//
// One rule across the sections that talk to something, and it is the reason
// this screen is worth having: nothing is saved until it has been PROVED.
// Each save runs the same probe the Test button runs, and a target that
// cannot authenticate is refused rather than stored. Secrets are write-only —
// the fields are always blank, and blank on save means "keep what is stored".
// Public URLs have nothing to connect to, so they are previewed instead:
// their failure mode is a well-formed link to the wrong host.
//
// Each tab leads with a status card — what is in force and where it came
// from, the design's rows — and then the form that changes it. Stripe's mode
// is read from the key, never switched (§4: no mode toggle, no rotate, no
// Connect toggle); "Replace key" is the paste + Test + Verify & save flow.
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RowCard, SubTabs, Toggle } from '../../components/fs';
import {
  AiSettingsState, BounceMailboxState, CaptchaSettingsState, ProbeResult, RenderSettingsState, SettingsSource, SmtpHealth,
  SmtpSettingsState, StripeSettingsState, UrlLinkPreview, UrlSettingsState,
  clearAiSettings, clearBounceMailbox, clearCaptchaSettings, clearRenderSettings, clearSmtpSettings, clearStripeSettings,
  clearUrlSettings, getPlatformSettings, previewUrlSettings, saveAiSettings, saveBounceMailbox, saveCaptchaSettings,
  saveRenderSettings, saveSmtpSettings, saveStripeSettings, saveUrlSettings, testAiSettings, testBounceMailbox,
  testCaptchaSettings, testRenderSettings, testSmtpSettings, testStripeSettings,
} from '../../api/admin';
import { useToast } from '../../components/Toast';
import { SectionEdits, useDirty, useReportEdits, useUnsavedGuard } from '../../components/unsaved';
import { ago } from '../admin/SsoShared';
import { usePlatform } from './PlatformLayout';
import PlatformStorage, { FormCard, SourceRow, SourceTone, StatusCard } from './PlatformStorage';

type Tab = 'stripe' | 'email' | 'storage' | 'urls' | 'captcha' | 'domains' | 'ai';
const TABS: [Tab, string][] = [
  ['stripe', 'Stripe'], ['email', 'Email'], ['storage', 'Storage'], ['urls', 'Public URLs'], ['captcha', 'Captcha'],
  ['domains', 'Custom domains'], ['ai', 'AI'],
];

/** The outcome of a probe, in the words the server used. */
function ProbeLine({ probe }: { probe: ProbeResult | null }) {
  if (!probe) return null;
  return probe.ok
    ? <div className="ad-ok pl-svc-probe" role="status">{probe.detail || 'Connection verified.'}</div>
    : <div className="ad-err pl-svc-probe" role="status">{probe.error || 'The test failed.'}</div>;
}

/** Verify & save · Test · (spacer) · the two-click Clear — every section's button row. */
function ActionRow({ children }: { children: ReactNode }) {
  return <div className="pl-svc-actions">{children}</div>;
}

const sourceTone = (s: SettingsSource): SourceTone => (s === 'unset' ? 'off' : 'ok');

export default function PlatformSettings() {
  const { role } = usePlatform();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = TABS.some(([t]) => t === tabParam) ? (tabParam as Tab) : 'stripe';
  // Every route behind this page — the GETs included — is the Super Admin's
  // (`platform.storage`); anyone else is told so rather than shown a 403.
  const superadmin = role === 'superadmin';
  const readOnly = !superadmin;

  const [stripe, setStripe] = useState<StripeSettingsState | null>(null);
  const [smtp, setSmtp] = useState<SmtpSettingsState | null>(null);
  const [urls, setUrls] = useState<UrlSettingsState | null>(null);
  const [captcha, setCaptcha] = useState<CaptchaSettingsState | null>(null);
  const [render, setRender] = useState<RenderSettingsState | null>(null);
  const [ai, setAi] = useState<AiSettingsState | null>(null);
  const [bounce, setBounce] = useState<BounceMailboxState | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [edits, setEdits] = useState<SectionEdits | null>(null);

  const guard = useUnsavedGuard({
    dirty: !!edits?.dirty,
    what: 'Your changes to these platform settings',
    onSave: edits ? () => edits.save() : undefined,
  });
  const { confirm } = guard;
  const goToTab = useCallback(
    (t: Tab) => confirm(() => setParams({ tab: t }, { replace: true })),
    [confirm, setParams]
  );

  useEffect(() => {
    if (!superadmin) return;
    getPlatformSettings()
      .then((r) => {
        setStripe(r.stripe || null); setSmtp(r.smtp || null); setUrls(r.urls || null);
        setCaptcha(r.captcha || null); setRender(r.render || null); setAi(r.ai || null);
        setBounce(r.bounceMailbox || null);
        setLoaded(true);
      })
      .catch((e) => setLoadError(e.message || 'Could not load platform settings.'));
  }, [superadmin]);

  return (
    <div className="pl-page pl-services">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Platform services</h1>
          <div className="fs-page-sub">Seven services the platform depends on</div>
        </div>
      </div>

      {!superadmin ? (
        <section className="fs-card pl-svc-locked" role="note">
          <div className="st-card-body">
            <b>Platform services need the Super Admin platform role.</b>{' '}
            <span className="st-muted">
              Stripe, email, upload storage, the public URLs, the captcha, custom domains and AI are configured by a
              Super Admin; ask one if something here needs changing.
            </span>
          </div>
        </section>
      ) : (
        <>
          <SubTabs
            aria-label="Services"
            className="pl-svc-tabs"
            items={TABS.map(([t, label]) => ({ label, active: tab === t, onClick: () => goToTab(t) }))}
          />

          {loadError && <div className="ad-err">{loadError}</div>}
          {/* Storage loads itself; every other tab waits on the one read. */}
          {!loaded && !loadError && tab !== 'storage' && (
            <section className="fs-card"><div className="st-card-body st-muted">Loading…</div></section>
          )}

          {tab === 'stripe' && stripe && <StripeSection state={stripe} onState={setStripe} readOnly={readOnly} onEdits={setEdits} />}
          {tab === 'email' && smtp && (
            <EmailSection
              state={smtp} onState={setSmtp} bounce={bounce} onBounce={setBounce} readOnly={readOnly} onEdits={setEdits}
            />
          )}
          {tab === 'storage' && <PlatformStorage onEdits={setEdits} />}
          {tab === 'urls' && urls && <UrlsSection state={urls} onState={setUrls} readOnly={readOnly} onEdits={setEdits} />}
          {tab === 'captcha' && captcha && <CaptchaSection state={captcha} onState={setCaptcha} readOnly={readOnly} onEdits={setEdits} />}
          {tab === 'domains' && render && <RenderSection state={render} onState={setRender} readOnly={readOnly} onEdits={setEdits} />}
          {tab === 'ai' && ai && <AiSection state={ai} onState={setAi} readOnly={readOnly} onEdits={setEdits} />}
        </>
      )}
      {guard.node}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe
// ---------------------------------------------------------------------------

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Receiving events · last one 4m ago" — the same three states the Plans page's webhook pill has. */
function WebhookHealth({ webhook }: { webhook: NonNullable<StripeSettingsState['webhook']> }) {
  const last = webhook.lastEventAt ? ago(webhook.lastEventAt) : '';
  return (
    <>
      {webhook.receivingEvents === null ? (
        <span className="fs-pill neutral">no events yet</span>
      ) : webhook.receivingEvents ? (
        <span className="fs-pill ok">receiving events</span>
      ) : (
        <span className="fs-pill danger">not receiving events</span>
      )}
      {last && (
        <span className="pl-svc-when" title={new Date(webhook.lastEventAt!).toLocaleString()}> last event {last}</span>
      )}
    </>
  );
}

function StripeSection({ state, onState, readOnly, onEdits }: {
  state: StripeSettingsState;
  onState: (s: StripeSettingsState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [connectWebhookSecret, setConnectWebhookSecret] = useState('');
  const [portalConfigId, setPortalConfigId] = useState(state.platform?.portalConfigId || '');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);
  const keyRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const snapshot = useMemo(
    () => ({ secretKey, webhookSecret, connectWebhookSecret, portalConfigId }),
    [secretKey, webhookSecret, connectWebhookSecret, portalConfigId]
  );
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: StripeSettingsState) => {
    onState(s);
    setSecretKey(''); setWebhookSecret(''); setConnectWebhookSecret('');
    setPortalConfigId(s.platform?.portalConfigId || '');
  };

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testStripeSettings(secretKey.trim() || undefined);
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveStripeSettings({
        secretKey: secretKey.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined,
        connectWebhookSecret: connectWebhookSecret.trim() || undefined,
        portalConfigId: portalConfigId.trim(),
      });
      apply(r.stripe);
      markSaved();
      toast(`Stripe saved and in force. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the keys
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearStripeSettings();
      apply(r.stripe);
      markSaved();
      toast('Cleared — Stripe follows the environment variables, or is disconnected without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  /** "Replace key" is the existing flow: the key field, Test, then Verify & save. */
  const onReplaceKey = () => keyRef.current?.focus();

  const onCopyWebhook = (url: string) => {
    navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  // The mode of the key in force, env or platform — inferred from the key.
  const mode = state.source === 'unset' ? null : (state.mode ?? state.platform?.keyMode ?? null);
  const p = state.platform;

  return (
    <>
      <StatusCard title="Stripe">
        <RowCard label="Mode" hint="Read from the key in force — there is no switch.">
          {state.source === 'unset'
            ? <span className="fs-pill neutral">Not connected</span>
            : mode === 'live' ? <span className="fs-pill ok">Live</span>
              : mode === 'test' ? <span className="fs-pill caution">Test</span>
                : <span className="fs-pill neutral">Unknown mode</span>}
        </RowCard>
        <RowCard
          label="Secret key"
          actions={readOnly ? undefined : (
            <button type="button" className="fs-btn text" onClick={onReplaceKey}>
              {state.source === 'unset' ? 'Add key' : 'Replace key'}
            </button>
          )}
        >
          {state.source === 'platform' ? (
            <>
              <span className="st-mono">{p?.keyLast4 ? `••••${p.keyLast4}` : '••••'}</span>
              <span className="st-muted"> · saved here</span>
            </>
          ) : state.source === 'env' ? (
            <>
              <span>From the environment</span>
              <div className="pl-svc-blurb">Using STRIPE_SECRET_KEY from the environment. Saving a key here overrides it.</div>
            </>
          ) : (
            <>
              <span className="st-state off">Not set</span>
              <div className="pl-svc-blurb">
                No Stripe key is set, so nothing can be charged: paid plans, Checkout and the billing portal are all inert.
              </div>
            </>
          )}
        </RowCard>
        <RowCard
          label="Webhooks"
          actions={state.webhook?.url ? (
            <button type="button" className="fs-btn neutral sm" onClick={() => onCopyWebhook(state.webhook!.url)}>
              {copied ? 'Copied' : 'Copy URL'}
            </button>
          ) : undefined}
        >
          {state.webhook && <div><WebhookHealth webhook={state.webhook} /></div>}
          {state.webhook?.url && <div className="st-mono pl-svc-url">{state.webhook.url}</div>}
          {p && (p.hasWebhookSecret
            ? <div className="pl-svc-blurb">A webhook signing secret is set.</div>
            : (
              <div className="pl-svc-blurb warn">
                No webhook signing secret — Checkout will charge people and this app will never hear about it.
              </div>
            ))}
          {!state.webhook && !p && <span className="st-muted">—</span>}
        </RowCard>
        {state.connect && (
          <RowCard label="Connect" hint="Customers taking payments on their own forms.">
            {state.connect.available
              ? <>Available — {plural(state.connect.accounts, 'account')} connected, {state.connect.chargesEnabled} taking payments</>
              : <span className="st-state off">Needs a Stripe key</span>}
          </RowCard>
        )}
      </StatusCard>

      <FormCard title="Keys">
        {error && <div className="ad-err">{error}</div>}
        <ProbeLine probe={probe} />

        <div className="fs-field">
          <label htmlFor="svc-stripe-key">Secret key</label>
          <input id="svc-stripe-key" ref={keyRef} type="password" value={secretKey} disabled={readOnly} autoComplete="new-password"
            placeholder={p?.keyLast4 ? `•••••••• (saved, ends ${p.keyLast4} — leave blank to keep)` : 'sk_live_… or sk_test_…'}
            onChange={(e) => setSecretKey(e.target.value)} />
          <div className="hint">Found in Stripe under Developers → API keys. The mode (live or test) is read from the key itself.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-stripe-whsec">Webhook signing secret</label>
          <input id="svc-stripe-whsec" type="password" value={webhookSecret} disabled={readOnly} autoComplete="new-password"
            placeholder={p?.hasWebhookSecret ? '•••••••• (saved — leave blank to keep)' : 'whsec_…'}
            onChange={(e) => setWebhookSecret(e.target.value)} />
          <div className="hint">
            Create an endpoint for <code>POST /api/stripe/webhook</code> in Stripe and paste its signing secret.
            Without it, payments succeed but no subscription, invoice or plan change is ever mirrored back.
          </div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-stripe-connect-whsec">Connect webhook signing secret (optional)</label>
          <input id="svc-stripe-connect-whsec" type="password" value={connectWebhookSecret} disabled={readOnly} autoComplete="new-password"
            placeholder={p?.hasConnectWebhookSecret ? '•••••••• (saved — leave blank to keep)' : 'whsec_…'}
            onChange={(e) => setConnectWebhookSecret(e.target.value)} />
          <div className="hint">
            Only if customers take payments on their own forms. Stripe sends connected accounts&apos; events to a
            separate endpoint with its own secret: create a second endpoint for the same URL, set to listen to
            events on Connected accounts, and paste its secret here. Without it a customer&apos;s payment setup is
            confirmed when they return to the app.
          </div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-stripe-portal">Billing portal configuration ID (optional)</label>
          <input id="svc-stripe-portal" value={portalConfigId} disabled={readOnly} placeholder="bpc_…"
            onChange={(e) => setPortalConfigId(e.target.value)} />
          <div className="hint">
            Blank uses Form-Magic&apos;s own configuration: card, invoices and billing details only. An ID here
            replaces it, with whatever that configuration allows.
          </div>
        </div>

        <ActionRow>
          <button className="fs-btn primary" disabled={busy || readOnly || (!secretKey.trim() && !p)}
            onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>Test connection</button>
          <span className="pl-svc-spacer" />
          {p && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — disconnect Stripe' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>
        <p className="pl-svc-foot">
          Saving makes a real authenticated call to Stripe first — a key that does not work is refused
          rather than stored. Keys are encrypted at rest and never shown again.
        </p>
      </FormCard>
    </>
  );
}

// ---------------------------------------------------------------------------
// Captcha (Cloudflare Turnstile) — U6
// ---------------------------------------------------------------------------

function CaptchaSection({ state, onState, readOnly, onEdits }: {
  state: CaptchaSettingsState;
  onState: (s: CaptchaSettingsState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [siteKey, setSiteKey] = useState(state.platform?.siteKey || '');
  const [secret, setSecret] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(() => ({ siteKey, secret }), [siteKey, secret]);
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: CaptchaSettingsState) => {
    onState(s);
    setSiteKey(s.platform?.siteKey || '');
    setSecret('');
  };

  const source = (): [string, string] => {
    if (state.source === 'platform') {
      return ['Captcha available',
        `Cloudflare Turnstile, with the keys saved here (site key ${state.platform?.siteKey || '—'}). Each form switches it on under Access → Bots and repeat responses.`];
    }
    if (state.source === 'env') {
      return ['Captcha available (environment)',
        'Using TURNSTILE_SITE_KEY and TURNSTILE_SECRET_KEY from the environment. Saving keys here overrides them.'];
    }
    return ['No captcha',
      'No Turnstile keys are set, so every form’s captcha switch is shown disabled. Forms still have the honeypot and the rate limit.'];
  };
  const [title, blurb] = source();

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testCaptchaSettings({ siteKey: siteKey.trim() || undefined, secret: secret || undefined });
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveCaptchaSettings({ siteKey: siteKey.trim(), secret: secret || undefined });
      apply(r.captcha);
      markSaved();
      toast(`Captcha keys saved and in force. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the keys
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearCaptchaSettings();
      apply(r.captcha);
      markSaved();
      toast('Cleared — the captcha follows the environment variables, or is unavailable without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <StatusCard title="Captcha">
        <SourceRow tone={sourceTone(state.source)} title={title} blurb={blurb} />
      </StatusCard>
      <FormCard title="Turnstile keys">
        {error && <div className="ad-err">{error}</div>}
        <ProbeLine probe={probe} />

        <div className="fs-field">
          <label htmlFor="svc-captcha-provider">Provider</label>
          <input id="svc-captcha-provider" value="Cloudflare Turnstile" disabled />
          <div className="hint">Free, no image puzzles, and most people never click anything. Create a widget at dash.cloudflare.com → Turnstile and add every hostname forms are served from.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-captcha-site">Site key</label>
          <input id="svc-captcha-site" value={siteKey} disabled={readOnly} placeholder="0x4AAAAAAA…"
            onChange={(e) => setSiteKey(e.target.value)} />
          <div className="hint">Public — it ships to every browser that opens a captcha-protected form.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-captcha-secret">Secret key</label>
          <input id="svc-captcha-secret" type="password" value={secret} disabled={readOnly} autoComplete="new-password"
            placeholder={state.platform?.hasSecret ? '•••••••• (saved — leave blank to keep)' : '0x4AAAAAAA…'}
            onChange={(e) => setSecret(e.target.value)} />
          <div className="hint">Encrypted at rest and never shown again.</div>
        </div>

        <ActionRow>
          <button className="fs-btn primary" disabled={busy || readOnly || !siteKey.trim() || (!secret && !state.platform?.hasSecret)}
            onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>Test secret</button>
          <span className="pl-svc-spacer" />
          {state.platform && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — remove the captcha keys' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>
        <p className="pl-svc-foot">
          Saving asks Cloudflare to check the secret first — a secret it does not recognise is refused rather
          than stored. The site key has no server-side check: a wrong one shows up as a widget that will not
          draw on the form.
        </p>
      </FormCard>
    </>
  );
}

// ---------------------------------------------------------------------------
// Render (custom domains) — T5
// ---------------------------------------------------------------------------

function RenderSection({ state, onState, readOnly, onEdits }: {
  state: RenderSettingsState;
  onState: (s: RenderSettingsState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [serviceId, setServiceId] = useState(state.platform?.serviceId || '');
  const [cnameTarget, setCnameTarget] = useState(state.platform?.cnameTarget || '');
  const [apiKey, setApiKey] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(() => ({ serviceId, cnameTarget, apiKey }), [serviceId, cnameTarget, apiKey]);
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: RenderSettingsState) => {
    onState(s);
    setServiceId(s.platform?.serviceId || '');
    setCnameTarget(s.platform?.cnameTarget || '');
    setApiKey('');
  };

  const source = (): [string, string] => {
    if (state.source === 'platform') {
      return ['Custom domains available',
        `Customer domains register against service ${state.platform?.serviceId || '—'}, CNAME target ${state.platform?.cnameTarget || '—'}. Accounts add their domain under Settings → Account access.`];
    }
    if (state.source === 'env') {
      return ['Custom domains available (environment)',
        'Using RENDER_API_KEY, RENDER_SERVICE_ID and CUSTOM_DOMAIN_CNAME from the environment. Saving values here overrides them.'];
    }
    return ['Custom domains off',
      'No Render credentials are set, so every account’s Custom domain card says the feature is not set up. Everything else works without them.'];
  };
  const [title, blurb] = source();

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testRenderSettings({
        serviceId: serviceId.trim() || undefined, cnameTarget: cnameTarget.trim() || undefined, apiKey: apiKey || undefined,
      });
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveRenderSettings({ serviceId: serviceId.trim(), cnameTarget: cnameTarget.trim(), apiKey: apiKey || undefined });
      apply(r.render);
      markSaved();
      toast(`Render settings saved and in force. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the key
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearRenderSettings();
      apply(r.render);
      markSaved();
      toast('Cleared — custom domains follow the environment variables, or are off without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <StatusCard title="Custom domains">
        <SourceRow tone={sourceTone(state.source)} title={title} blurb={blurb} />
      </StatusCard>
      <FormCard title="Render service">
        {error && <div className="ad-err">{error}</div>}
        <ProbeLine probe={probe} />

        <div className="fs-field">
          <label htmlFor="svc-render-key">Render API key</label>
          <input id="svc-render-key" type="password" value={apiKey} disabled={readOnly} autoComplete="new-password"
            placeholder={state.platform?.hasApiKey ? '•••••••• (saved — leave blank to keep)' : 'rnd_…'}
            onChange={(e) => setApiKey(e.target.value)} />
          <div className="hint">From dashboard.render.com → Account settings → API keys. Encrypted at rest and never shown again.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-render-service">Service ID</label>
          <input id="svc-render-service" value={serviceId} disabled={readOnly} placeholder="srv-…"
            onChange={(e) => setServiceId(e.target.value)} />
          <div className="hint">The web service customer domains attach to — the id in the service&rsquo;s dashboard URL.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-render-cname">CNAME target</label>
          <input id="svc-render-cname" value={cnameTarget} disabled={readOnly} placeholder="form-magic.onrender.com"
            onChange={(e) => setCnameTarget(e.target.value)} />
          <div className="hint">What customers point their DNS at — the service&rsquo;s own onrender.com hostname.</div>
        </div>

        <ActionRow>
          <button className="fs-btn primary"
            disabled={busy || readOnly || !serviceId.trim() || !cnameTarget.trim() || (!apiKey && !state.platform?.hasApiKey)}
            onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>Test key</button>
          <span className="pl-svc-spacer" />
          {state.platform && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — remove the Render settings' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>
        <p className="pl-svc-foot">
          Saving makes a real authenticated read of the service&rsquo;s domains first — a key or service id
          Render does not recognise is refused rather than stored. The CNAME target has no server-side
          check: a wrong one shows up as customer DNS that never verifies.
        </p>
      </FormCard>
    </>
  );
}

// ---------------------------------------------------------------------------
// AI (the form builder)
// ---------------------------------------------------------------------------

/** Mirrors AI_MODELS in server/src/services/aiFormBuilder.ts. */
const AI_MODEL_OPTIONS = [
  { id: 'claude-opus-5', label: 'Claude Opus 5 — most capable' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5 — balanced' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 — fastest' },
];

function AiSection({ state, onState, readOnly, onEdits }: {
  state: AiSettingsState;
  onState: (s: AiSettingsState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [model, setModel] = useState(state.platform?.model || AI_MODEL_OPTIONS[0].id);
  const [apiKey, setApiKey] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(() => ({ model, apiKey }), [model, apiKey]);
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: AiSettingsState) => {
    onState(s);
    setModel(s.platform?.model || AI_MODEL_OPTIONS[0].id);
    setApiKey('');
  };

  const source = (): [string, string] => {
    if (state.source === 'platform') {
      return ['AI form building on',
        `Every account can describe a form and get a draft, generated with ${state.platform?.model || '—'}. The key and model live here only — customers never see either.`];
    }
    if (state.source === 'env') {
      return ['AI form building on (environment)',
        'Using AI_API_KEY and AI_MODEL from the environment. Saving values here overrides them.'];
    }
    return ['AI form building off',
      'No AI key is set, so the "Create with AI" entry point does not appear for any account. Everything else works without it.'];
  };
  const [title, blurb] = source();

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testAiSettings({ provider: 'anthropic', model, apiKey: apiKey || undefined });
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveAiSettings({ provider: 'anthropic', model, apiKey: apiKey || undefined });
      apply(r.ai);
      markSaved();
      toast(`AI settings saved and in force. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the key
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearAiSettings();
      apply(r.ai);
      markSaved();
      toast('Cleared — AI form building follows the environment variables, or is off without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <StatusCard title="AI form building">
        <SourceRow tone={sourceTone(state.source)} title={title} blurb={blurb} />
      </StatusCard>
      <FormCard title="Provider and model">
        {error && <div className="ad-err">{error}</div>}
        <ProbeLine probe={probe} />

        <div className="fs-field">
          <label htmlFor="svc-ai-provider">Provider</label>
          <select id="svc-ai-provider" value="anthropic" disabled>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
          <div className="hint">The platform generating forms. Anthropic is the provider implemented today.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-ai-key">API key</label>
          <input id="svc-ai-key" type="password" value={apiKey} disabled={readOnly} autoComplete="new-password"
            placeholder={state.platform?.hasApiKey ? '•••••••• (saved — leave blank to keep)' : 'sk-ant-…'}
            onChange={(e) => setApiKey(e.target.value)} />
          <div className="hint">From console.anthropic.com → API keys. Encrypted at rest, never shown again, and every
            account&rsquo;s generations spend against it.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-ai-model">Model</label>
          <select id="svc-ai-model" value={model} disabled={readOnly} onChange={(e) => setModel(e.target.value)}>
            {AI_MODEL_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <div className="hint">What generates the drafts. Opus writes the best forms; Haiku is cheapest per generation.</div>
        </div>

        <ActionRow>
          <button className="fs-btn primary"
            disabled={busy || readOnly || (!apiKey && !state.platform?.hasApiKey)}
            onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>Test key</button>
          <span className="pl-svc-spacer" />
          {state.platform && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — remove the AI settings' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>
        <p className="pl-svc-foot">
          Saving retrieves the chosen model with the key first — a key the provider rejects, or a model it
          cannot see, is refused rather than stored. The probe spends no tokens; generations later will.
        </p>
      </FormCard>
    </>
  );
}

// ---------------------------------------------------------------------------
// SMTP
// ---------------------------------------------------------------------------

/**
 * The status card's Health row: the MARKETING bounce rate over 30 days
 * (tracking, 072), amber from 2% and red from 5%, and whether it is rising or
 * falling against the 30 days before. The tone reads the rate as shown, so a
 * "5%" is never amber.
 */
function HealthRow({ health, reading }: { health: SmtpHealth; reading: boolean }) {
  const { sent30d: sent, bounced30d: bounced } = health;
  const rate = Math.round((health.ratePct ?? (sent ? (bounced / sent) * 100 : 0)) * 10) / 10;
  const tone = rate >= 5 ? 'bad' : rate >= 2 ? 'warn' : 'ok';
  const prev = health.prevRatePct === null ? '' : `It was ${Math.round(health.prevRatePct * 10) / 10}% in the 30 days before.`;
  return (
    <RowCard label="Health">
      {sent === 0 ? (
        <span className="st-muted">No marketing email in the last 30 days</span>
      ) : (
        <>
          <span className={`st-state ${tone}`}>
            Bounce rate {rate}% · {bounced.toLocaleString()} of {sent.toLocaleString()} marketing emails in the last 30 days
          </span>
          {health.trend === 'rising' && <span className="fs-pill warn pl-svc-trend" title={prev}>rising</span>}
          {health.trend === 'falling' && <span className="fs-pill ok pl-svc-trend" title={prev}>falling</span>}
        </>
      )}
      {/* Without the mailbox nothing is read, so a 0% would be a claim nobody checked. */}
      {!reading && <div className="pl-svc-blurb">Bounces are counted only while the bounce mailbox below is set up.</div>}
    </RowCard>
  );
}

function EmailSection({ state, onState, bounce, onBounce, readOnly, onEdits }: {
  state: SmtpSettingsState;
  onState: (s: SmtpSettingsState) => void;
  /** Tracking (072): the bounce mailbox — null from a server that sends none, read as not set up */
  bounce: BounceMailboxState | null;
  onBounce: (s: BounceMailboxState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [host, setHost] = useState(state.platform?.host || '');
  const [port, setPort] = useState(String(state.platform?.port ?? 587));
  const [secure, setSecure] = useState(!!state.platform?.secure);
  const [user, setUser] = useState(state.platform?.user || '');
  const [pass, setPass] = useState('');
  const [from, setFrom] = useState(state.platform?.from || '');
  const [sendTo, setSendTo] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  // sendTo is left out on purpose: it addresses the TEST, and nothing about it
  // is stored, so a typed address is not unsaved work.
  const snapshot = useMemo(
    () => ({ host, port, secure, user, pass, from }),
    [host, port, secure, user, pass, from]
  );
  const { dirty, markSaved } = useDirty(snapshot);
  // The tab holds two forms, the mail server and the bounce mailbox, and the
  // guard asks about either: one report up, and "Save and leave" saves
  // whichever has unsaved work.
  const [bounceEdits, setBounceEdits] = useState<SectionEdits | null>(null);
  useReportEdits(onEdits, dirty || !!bounceEdits?.dirty, async () => {
    if (dirty) await onSave();
    if (bounceEdits?.dirty) await bounceEdits.save();
  });

  const apply = (s: SmtpSettingsState) => {
    // A save's answer need not carry the 30-day figures; changing the mail
    // server does not change them.
    onState(s.health || !state.health ? s : { ...s, health: state.health });
    setHost(s.platform?.host || '');
    setPort(String(s.platform?.port ?? 587));
    setSecure(!!s.platform?.secure);
    setUser(s.platform?.user || '');
    setFrom(s.platform?.from || '');
    setPass('');
  };

  // What the status card reads: the server in force, and where it came from.
  const p = state.platform;
  const env = state.env;
  const hostLine = state.source === 'platform' && p
    ? `${p.host}:${p.port}${p.secure ? ' (TLS)' : ''}`
    : state.source === 'env' && env ? `${env.host}:${env.port}` : null;
  const fromLine = state.source === 'platform' && p ? p.from : state.source === 'env' && env ? env.from : null;
  const source = (): [string, string | undefined] => {
    if (state.source === 'platform') return ['Sending through the saved mail server', undefined];
    if (state.source === 'env') {
      return ['Sending through the environment',
        `Using SMTP_HOST${env ? ` (${env.host}:${env.port}, from ${env.from})` : ''}. Saving here overrides it.`];
    }
    return ['No mail server',
      'Nothing can be emailed: magic-link sign-ins, trial reminders, submission notifications and confirmations are all silently unavailable.'];
  };
  const [title, blurb] = source();

  const body = () => ({
    host: host.trim(),
    port: Number(port) || 587,
    secure,
    user: user.trim() || undefined,
    pass: pass || undefined,
    from: from.trim() || undefined,
  });

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testSmtpSettings({ ...(host.trim() ? body() : {}), sendTo: sendTo.trim() || undefined });
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveSmtpSettings(body());
      apply(r.smtp);
      markSaved();
      toast(`Mail server saved and in force. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the setup
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearSmtpSettings();
      apply(r.smtp);
      markSaved();
      toast('Cleared — email follows the environment variables, or is unavailable without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <StatusCard title="Email (SMTP)">
        <RowCard label="Host">
          {hostLine ? <span className="st-mono">{hostLine}</span> : <span className="st-state off">Not set</span>}
        </RowCard>
        <RowCard label="From">
          {fromLine ? <span className="st-mono">{fromLine}</span> : <span className="st-muted">—</span>}
        </RowCard>
        {state.health && <HealthRow health={state.health} reading={!!bounce?.configured} />}
        <SourceRow tone={sourceTone(state.source)} title={title} blurb={blurb} />
      </StatusCard>

      <FormCard title="Mail server">
        {error && <div className="ad-err">{error}</div>}
        <ProbeLine probe={probe} />

        <div className="pl-svc-pair">
          <div className="fs-field">
            <label htmlFor="svc-smtp-host">Host</label>
            <input id="svc-smtp-host" value={host} disabled={readOnly} placeholder="smtp.sendgrid.net"
              onChange={(e) => setHost(e.target.value)} />
          </div>
          <div className="fs-field pl-svc-port">
            <label htmlFor="svc-smtp-port">Port</label>
            <input id="svc-smtp-port" inputMode="numeric" value={port} disabled={readOnly}
              onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ''))} />
          </div>
        </div>
        <label className="pl-svc-check">
          <input type="checkbox" checked={secure} disabled={readOnly} onChange={(e) => setSecure(e.target.checked)} />
          Use TLS from the start (port 465). Leave off for STARTTLS on 587.
        </label>
        <div className="pl-svc-pair even">
          <div className="fs-field">
            <label htmlFor="svc-smtp-user">Username</label>
            <input id="svc-smtp-user" value={user} disabled={readOnly} autoComplete="off" onChange={(e) => setUser(e.target.value)} />
            <div className="hint">Leave blank for a relay that needs no authentication.</div>
          </div>
          <div className="fs-field">
            <label htmlFor="svc-smtp-pass">Password</label>
            <input id="svc-smtp-pass" type="password" value={pass} disabled={readOnly} autoComplete="new-password"
              placeholder={p?.hasPass ? '•••••••• (saved — leave blank to keep)' : ''}
              onChange={(e) => setPass(e.target.value)} />
          </div>
        </div>
        <div className="fs-field">
          <label htmlFor="svc-smtp-from">From address</label>
          <input id="svc-smtp-from" value={from} disabled={readOnly} placeholder="forms@yourdomain.com"
            onChange={(e) => setFrom(e.target.value)} />
          <div className="hint">Every product email is sent from here. It must be an address this server is allowed to send as.</div>
        </div>

        <div className="fs-field pl-svc-sendto">
          <label htmlFor="svc-smtp-sendto">Send a test message to (optional)</label>
          <input id="svc-smtp-sendto" value={sendTo} placeholder="you@yourdomain.com" onChange={(e) => setSendTo(e.target.value)} />
          <div className="hint">Blank tests the connection and credentials only. With an address, a real email is sent — the only way to know the whole path works.</div>
        </div>

        <ActionRow>
          <button className="fs-btn primary" disabled={busy || readOnly || !host.trim()} onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>
            {sendTo.trim() ? 'Send test email' : 'Test connection'}
          </button>
          <span className="pl-svc-spacer" />
          {p && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — stop sending email' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>
        <p className="pl-svc-foot">
          Saving opens a real connection to the mail server first — a host or password that does not
          work is refused rather than stored. The password is encrypted at rest and never shown again.
        </p>
      </FormCard>

      <BounceMailboxCard
        state={bounce} onState={onBounce} smtpUser={(state.source === 'platform' ? p?.user : env?.user) || ''}
        readOnly={readOnly} onEdits={setBounceEdits}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Bounce mailbox (tracking, 072)
// ---------------------------------------------------------------------------

const GMAIL_IMAP = 'imap.gmail.com';

/**
 * Where bounce notices land: the sending mailbox, read over IMAP by the
 * server's poller every five minutes — read-only, never flagging, moving or
 * deleting anything — and matched to the marketing email they answer by its
 * Message-ID. Production sends through Google Workspace, so the defaults are
 * Gmail's and the login is the SMTP one unless the toggle is turned off.
 *
 * Same discipline as the rest of the page: Verify & save opens the mailbox
 * first and refuses one that will not open; the password is write-only. Its
 * buttons say "the bounce mailbox" to a screen reader, because the mail
 * server's own Verify & save and Test connection sit on the same tab.
 */
function BounceMailboxCard({ state, onState, smtpUser, readOnly, onEdits }: {
  state: BounceMailboxState | null;
  onState: (s: BounceMailboxState) => void;
  /** the username of the SMTP settings in force, which the toggle reuses ('' for a relay without one) */
  smtpUser: string;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const p = state?.platform ?? null;
  const [useSmtpLogin, setUseSmtpLogin] = useState(p?.useSmtpLogin ?? true);
  const [host, setHost] = useState(p?.host || GMAIL_IMAP);
  const [port, setPort] = useState(String(p?.port ?? 993));
  const [user, setUser] = useState(p?.user || '');
  const [pass, setPass] = useState('');
  const [mailbox, setMailbox] = useState(p?.mailbox || 'INBOX');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(
    () => ({ useSmtpLogin, host, port, user, pass, mailbox }),
    [useSmtpLogin, host, port, user, pass, mailbox]
  );
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: BounceMailboxState) => {
    onState(s);
    setUseSmtpLogin(s.platform?.useSmtpLogin ?? true);
    setHost(s.platform?.host || GMAIL_IMAP);
    setPort(String(s.platform?.port ?? 993));
    setUser(s.platform?.user || '');
    setMailbox(s.platform?.mailbox || 'INBOX');
    setPass('');
  };

  // With the SMTP login the server signs in as the mail server does, so
  // nothing of this form's own login is sent.
  const body = () => ({
    useSmtpLogin,
    host: host.trim(),
    port: Number(port) || 993,
    user: useSmtpLogin ? undefined : user.trim() || undefined,
    pass: useSmtpLogin ? undefined : pass || undefined,
    mailbox: mailbox.trim() || 'INBOX',
  });

  async function onTest() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await testBounceMailbox(body());
      setProbe(r.result);
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError(''); setProbe(null);
    try {
      const r = await saveBounceMailbox(body());
      apply(r.bounceMailbox);
      markSaved();
      toast(`Bounce mailbox saved — bounces are read from it every five minutes. ${r.detail || ''}`.trim());
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the setup
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setProbe(null);
    try {
      const r = await clearBounceMailbox();
      apply(r.bounceMailbox);
      markSaved();
      toast('Cleared — bounce notices are no longer read, so the bounce rate stops counting.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  const bounces = state ? `${plural(state.bounces30d, 'bounce')} in the last 30 days` : '';
  const status = !state?.configured
    ? <span className="st-state off">Not set up — bounce notices are not being read.</span>
    : state.lastError
      ? <span className="st-state bad">{state.lastError}</span>
      : state.lastCheckedAt
        ? <span title={new Date(state.lastCheckedAt).toLocaleString()}>Checked {ago(state.lastCheckedAt)} · {bounces}</span>
        : <span>Not checked yet · {bounces}</span>;

  return (
    <FormCard title="Bounce mailbox" className="pl-svc-bounce">
      <div className="pl-svc-bounce-status">{status}</div>
      <p className="pl-svc-lead">
        Bounce notices come back to the sending mailbox. They are read from it every five minutes, without
        changing anything in it, and a hard-bounced address is skipped by every later marketing send.
      </p>
      {error && <div className="ad-err">{error}</div>}
      <ProbeLine probe={probe} />

      <div>
        <Toggle checked={useSmtpLogin} disabled={readOnly} onChange={setUseSmtpLogin} label="Use the SMTP login" />
        <div className="pl-svc-blurb">
          {useSmtpLogin
            ? (smtpUser
              ? `Signs in as ${smtpUser} with the mail server’s password.`
              : 'The mail server in force has no username to reuse — turn this off and enter the mailbox’s own.')
            : 'Signs in with the username and password below.'}
        </div>
      </div>

      <div className="pl-svc-pair">
        <div className="fs-field">
          <label htmlFor="svc-bounce-host">IMAP host</label>
          <input id="svc-bounce-host" value={host} disabled={readOnly} placeholder={GMAIL_IMAP}
            onChange={(e) => setHost(e.target.value)} />
        </div>
        <div className="fs-field pl-svc-port">
          <label htmlFor="svc-bounce-port">IMAP port</label>
          <input id="svc-bounce-port" inputMode="numeric" value={port} disabled={readOnly}
            onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>
      </div>
      {!useSmtpLogin && (
        <div className="pl-svc-pair even">
          <div className="fs-field">
            <label htmlFor="svc-bounce-user">IMAP username</label>
            <input id="svc-bounce-user" value={user} disabled={readOnly} autoComplete="off" onChange={(e) => setUser(e.target.value)} />
          </div>
          <div className="fs-field">
            <label htmlFor="svc-bounce-pass">IMAP password</label>
            <input id="svc-bounce-pass" type="password" value={pass} disabled={readOnly} autoComplete="new-password"
              placeholder={p?.hasPass && !p.useSmtpLogin ? '•••••••• (saved — leave blank to keep)' : ''}
              onChange={(e) => setPass(e.target.value)} />
          </div>
        </div>
      )}
      <div className="fs-field pl-svc-mailbox">
        <label htmlFor="svc-bounce-mailbox">Mailbox</label>
        <input id="svc-bounce-mailbox" value={mailbox} disabled={readOnly} placeholder="INBOX"
          onChange={(e) => setMailbox(e.target.value)} />
        <div className="hint">The folder bounce notices arrive in — INBOX unless a filter moves them.</div>
      </div>

      <ActionRow>
        <button className="fs-btn primary"
          disabled={busy || readOnly || !host.trim() || (!useSmtpLogin && !user.trim())}
          onClick={() => void onSave().catch(() => undefined)}>
          {busy ? 'Working…' : <>Verify &amp; save<span className="fs-sr-only"> the bounce mailbox</span></>}
        </button>
        <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>
          Test connection<span className="fs-sr-only"> to the bounce mailbox</span>
        </button>
        <span className="pl-svc-spacer" />
        {state?.configured && !readOnly && (
          <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
            {confirmClear ? 'Confirm — stop reading bounces' : 'Clear bounce mailbox'}
          </button>
        )}
      </ActionRow>
      <p className="pl-svc-foot">
        Gmail and Google Workspace: turn IMAP on in Gmail’s settings (Forwarding and POP/IMAP), and sign in
        with an app password — the account’s own password is refused.
      </p>
    </FormCard>
  );
}

// ---------------------------------------------------------------------------
// Public URLs
// ---------------------------------------------------------------------------

const LINK_LABELS: [keyof UrlLinkPreview, string][] = [
  ['publicForm', 'A published form'],
  ['magicLink', 'An emailed sign-in link'],
  ['ssoCallback', 'The SSO callback your IdP must allow'],
  ['uploadedFile', 'An uploaded file'],
  ['stripeWebhook', 'The Stripe webhook endpoint'],
  ['stripeReturn', 'Where Stripe returns after Checkout'],
];

function UrlsSection({ state, onState, readOnly, onEdits }: {
  state: UrlSettingsState;
  onState: (s: UrlSettingsState) => void;
  readOnly: boolean;
  onEdits: (e: SectionEdits | null) => void;
}) {
  const [publicBaseUrl, setPublicBaseUrl] = useState(state.platform?.publicBaseUrl || '');
  const [appBaseUrl, setAppBaseUrl] = useState(state.platform?.appBaseUrl || '');
  const [links, setLinks] = useState<UrlLinkPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(() => ({ publicBaseUrl, appBaseUrl }), [publicBaseUrl, appBaseUrl]);
  const { dirty, markSaved } = useDirty(snapshot);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: UrlSettingsState) => {
    onState(s);
    setPublicBaseUrl(s.platform?.publicBaseUrl || '');
    setAppBaseUrl(s.platform?.appBaseUrl || '');
  };

  const source = (): [SourceTone, string, string] => {
    if (state.source === 'platform') {
      return ['ok', 'Set here', `Links are composed from ${state.effective?.publicBaseUrl || '(derived per request)'}.`];
    }
    if (state.source === 'env') {
      return ['ok', 'Set by the environment', `Using PUBLIC_BASE_URL (${state.effective?.publicBaseUrl}). Saving here overrides it.`];
    }
    // Not "off": a request-derived URL works until it is behind a proxy.
    return ['warn', 'Derived from each request',
      'Links are built from whatever Host header arrives. That works in development and breaks behind a proxy or a CDN — emailed links and SSO callbacks can point at the wrong origin.'];
  };
  const [tone, title, blurb] = source();

  const body = () => ({
    publicBaseUrl: publicBaseUrl.trim() || undefined,
    appBaseUrl: appBaseUrl.trim() || undefined,
  });

  async function onPreview() {
    setBusy(true); setError('');
    try {
      const r = await previewUrlSettings(body());
      setLinks(r.links);
    } catch (e: any) { setError(e.message || 'Preview failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError('');
    try {
      const r = await saveUrlSettings(body());
      apply(r.urls);
      setLinks(r.links);
      markSaved();
      toast('Public URLs saved — new links use these values.');
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the edit
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError(''); setLinks(null);
    try {
      const r = await clearUrlSettings();
      apply(r.urls);
      markSaved();
      toast('Cleared — URLs follow the environment variables, or each request without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <StatusCard title="Public URLs">
        <RowCard label="Public URL">
          {state.effective?.publicBaseUrl
            ? <span className="st-mono">{state.effective.publicBaseUrl}</span>
            : <span className="st-muted">Derived per request</span>}
        </RowCard>
        <SourceRow tone={tone} title={title} blurb={blurb} />
      </StatusCard>

      <FormCard title="Addresses">
        {error && <div className="ad-err">{error}</div>}

        <div className="fs-field">
          <label htmlFor="svc-url-public">Public URL</label>
          <input id="svc-url-public" value={publicBaseUrl} disabled={readOnly} placeholder="https://forms.yourdomain.com"
            onChange={(e) => setPublicBaseUrl(e.target.value)} />
          <div className="hint">Where respondents reach this app. Composes form links, emailed sign-in links, SSO callbacks and uploaded-file URLs. Blank derives it from each request.</div>
        </div>

        <div className="fs-field">
          <label htmlFor="svc-url-app">Console URL (for Stripe returns)</label>
          <input id="svc-url-app" value={appBaseUrl} disabled={readOnly} placeholder="https://forms.yourdomain.com"
            onChange={(e) => setAppBaseUrl(e.target.value)} />
          <div className="hint">Where customers land after Checkout or the billing portal. Usually the same as the public URL — they differ only when the console is served from somewhere else (in development, the Vite dev server).</div>
        </div>

        <ActionRow>
          <button className="fs-btn primary" disabled={busy || readOnly} onClick={() => void onSave().catch(() => undefined)}>
            {busy ? 'Working…' : 'Save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onPreview()}>Preview links</button>
          <span className="pl-svc-spacer" />
          {state.platform && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — back to env/request' : 'Clear platform settings'}
            </button>
          )}
        </ActionRow>

        {links && (
          <div className="pl-svc-preview">
            <h3>What these produce</h3>
            <p className="pl-svc-foot">
              There is nothing to connect to here, so this is the proof that matters: the actual links,
              before one goes out in an email or into an identity provider's allow-list.
            </p>
            <div className="pl-scroll">
              <table className="fs-table pl-urls">
                <tbody>
                  {LINK_LABELS.map(([key, label]) => (
                    <tr key={key}>
                      <td className="pl-urlrow">{label}</td>
                      <td><code className="pl-urlcode">{links[key]}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </FormCard>
    </>
  );
}
